import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import type { Database } from "@/integrations/supabase/types";

// Shape returned by the RPC function get_encounter_reviews_by_reviewer
type RpcReviewerRow = Database["public"]["Functions"]["get_encounter_reviews_by_reviewer"]["Returns"][number];

export interface ReviewerStatRowAggregated {
  reviewer_id: string | null;
  display_name: string | null;
  display_color: string | null;
  approved_count: number;
  rejected_count: number;
  total_count: number;
  customer_breakdown: CustomerBreakdownRow[];
}

export interface CustomerBreakdownRow {
  customer_id: string | null;
  approved_count: number;
  rejected_count: number;
  total_count: number;
}

interface UseReviewerBreakdownOptions {
  customerIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

export function useReviewerBreakdown(options?: UseReviewerBreakdownOptions) {
  const { customerIds, dateFrom, dateTo } = options || {};
  const { data, isLoading, error } = useQuery<ReviewerStatRowAggregated[]>({
    queryKey: ["get-encounter-reviews-by-reviewer", customerIds?.join(","), dateFrom, dateTo],
    queryFn: async () => {
      // Single call is sufficient (few reviewers and grouped by reviewer/customer)
      const { data: page, error: pageError } = await supabase.rpc("get_encounter_reviews_by_reviewer", {
        encounter_from: dateFrom,
        encounter_to: dateTo,
        customer_ids: customerIds && customerIds.length > 0 ? customerIds : undefined,
        // Use a generous limit to comfortably cover all grouped rows
        limit_count: 1000,
        offset_count: 0,
      });

      if (pageError) throw pageError;

      const allRows: RpcReviewerRow[] = page ?? [];

      // Fetch profile metadata for reviewers to get display_name and display_color
      const reviewerIds = Array.from(
        new Set(allRows.map((r) => r.reviewer_id).filter((id): id is string => id != null)),
      );

      let profileMap = new Map<string, { display_name: string | null; display_color: string | null }>();
      if (reviewerIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("id, display_name, display_color")
          .in("id", reviewerIds as string[]);
        if (profilesError) throw profilesError;
        const profilesMeta = (profiles ?? []) as Array<{
          id: string;
          display_name: string | null;
          display_color: string | null;
        }>;
        profileMap = new Map(
          profilesMeta.map((p) => [
            p.id,
            {
              display_name: p.display_name ?? null,
              display_color: p.display_color ?? null,
            },
          ]),
        );
      }

      // Aggregate by reviewer_id across customers and retain per-customer rows
      const byReviewer = new Map<string, ReviewerStatRowAggregated>();
      for (const r of allRows) {
        // Handle potential nulls even though types say non-nullable
        const reviewerId = r.reviewer_id ?? null;
        const customerId = r.customer_id ?? null;
        const key = reviewerId || "unknown";

        const approved = r.approved_count ?? 0;
        const rejected = r.rejected_count ?? 0;
        const total = r.total_count ?? approved + rejected;

        let existing = byReviewer.get(key);
        if (!existing) {
          const profile = reviewerId ? profileMap.get(reviewerId) : undefined;
          existing = {
            reviewer_id: reviewerId,
            display_name: profile?.display_name ?? null,
            display_color: profile?.display_color ?? null,
            approved_count: 0,
            rejected_count: 0,
            total_count: 0,
            customer_breakdown: [],
          };
          byReviewer.set(key, existing);
        }

        // accumulate reviewer totals
        existing.approved_count += approved;
        existing.rejected_count += rejected;
        existing.total_count += total;

        // push per-customer breakdown row
        existing.customer_breakdown.push({
          customer_id: customerId,
          approved_count: approved,
          rejected_count: rejected,
          total_count: total,
        });
      }

      const aggregated = Array.from(byReviewer.values()).sort((a, b) => (b.total_count ?? 0) - (a.total_count ?? 0));

      return aggregated;
    },
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    rows: data,
    isLoading,
    error,
  };
}
